/**
 * Adaptadores Firebase (Web SDK v11 modular) que implementan las abstracciones
 * del core. Aislados aquí para que el resto de `rider-core` sea testeable sin
 * Firebase. Se importan como `@ibercar/rider-core/firebase`.
 *
 * NO se cubren con unit tests (requieren proyecto vivo); se validan en el spike
 * F0 y en tests de integración contra emuladores.
 */
import {
  reserveFromSnapshot,
  tripFromSnapshot,
  type ReserveRtdb,
  type TripRtdb,
} from '@ibercar/rtdb-contracts';
import type { FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getIdToken, type Auth } from 'firebase/auth';
import { onValue, ref, type Database, type DataSnapshot, type Unsubscribe } from 'firebase/database';
import { fetchAndActivate, getBoolean, type RemoteConfig } from 'firebase/remote-config';
import { FLAG_DEFAULTS, type FlagName, type RemoteFlags } from '../flags';
import type { IdTokenProvider } from '../auth/session';
export * from './push';

/** IdTokenProvider sobre Firebase Auth para {@link NestAuthSession}. */
export function createFirebaseIdTokenProvider(auth: Auth): IdTokenProvider {
  return async () => {
    const user = auth.currentUser;
    if (!user) return null;
    return getIdToken(user);
  };
}

/** RemoteFlags sobre Firebase Remote Config. */
export class RemoteConfigFlags implements RemoteFlags {
  constructor(private readonly rc: RemoteConfig) {}
  getBool(name: FlagName): boolean {
    return getBoolean(this.rc, name);
  }
}

/**
 * Configura Remote Config (defaults + intervalos) y activa. No-fatal: ante fallo
 * de red la app sigue con los defaults locales (paridad con el original).
 */
export async function bootstrapRemoteConfig(
  rc: RemoteConfig,
  opts: { minimumFetchIntervalMillis: number; fetchTimeoutMillis: number },
): Promise<void> {
  rc.settings.minimumFetchIntervalMillis = opts.minimumFetchIntervalMillis;
  rc.settings.fetchTimeoutMillis = opts.fetchTimeoutMillis;
  rc.defaultConfig = { ...FLAG_DEFAULTS };
  try {
    await fetchAndActivate(rc);
  } catch {
    /* no-fatal: defaults locales */
  }
}

/** Activa App Check con reCAPTCHA v3 (web). No-fatal. */
export function activateAppCheckWeb(app: FirebaseApp, siteKey: string): void {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch {
    /* no-fatal */
  }
}

// ── Streams RTDB en tiempo real (SIEMPRE leen de RTDB; regla de oro) ───────────

/** Suscribe a un ride: `Ride Requests/{rideId}`. Devuelve la función de baja. */
export function watchTrip(db: Database, rideId: string, cb: (trip: TripRtdb) => void): Unsubscribe {
  const node = ref(db, `Ride Requests/${rideId}`);
  return onValue(node, (snap: DataSnapshot) => {
    cb(tripFromSnapshot(snap.key, snap.val()));
  });
}

/** Suscribe a una reserva: `Reserves/{reserveId}`. Devuelve la función de baja. */
export function watchReserve(db: Database, reserveId: string, cb: (reserve: ReserveRtdb) => void): Unsubscribe {
  const node = ref(db, `Reserves/${reserveId}`);
  return onValue(node, (snap: DataSnapshot) => {
    cb(reserveFromSnapshot(snap.key, snap.val()));
  });
}
